import type { ComponentProps } from 'react';

import { ConfirmActionButton } from './ConfirmDialog.js';

export interface ForfeitConfirmButtonProps {
  /** Whether `session` is a solo game (no opponent) — a purely platform-owned fact (`players.length <= 1`), never a game-specific one. */
  isSolo: boolean;
  /**
   * Total participant count — purely platform-owned (`session.players.length`),
   * never a game-specific fact. Drives accurate copy for a >2-player
   * session (docs/games/word-rush/GAME_PLAN.md §13.5): resigning from such
   * a session forfeits only the resigner, and the remaining players
   * continue, rather than ending everyone's game. The exact 2-player copy
   * ("your opponent will be recorded as the winner") is unchanged.
   */
  participantCount: number;
  isLoading: boolean;
  onConfirm: () => void | Promise<unknown>;
  triggerClassName?: string;
  triggerButtonProps?: ComponentProps<typeof ConfirmActionButton>['triggerButtonProps'];
}

/**
 * The resign/forfeit action, gated behind a confirming dialog that states
 * the real, server-implemented consequence (docs/APP_PLAN.md §8.6, §12
 * M6; docs/games/word-rush/GAME_PLAN.md §13.5): in a 2-player game the
 * resigning player immediately loses and the opponent is recorded as the
 * winner; in a >2-player game only the resigner forfeits and the remaining
 * players continue; in a solo game the session is recorded as abandoned.
 * Nothing here decides the outcome — the dialog only describes what `POST
 * /api/game-sessions/:id/forfeit` (`apps/api/src/services/forfeit.ts`)
 * actually does.
 *
 * The prompt's mechanics (focus, escape, destructive styling,
 * single-submission) come from the shared `ConfirmDialog`, the one
 * confirmation implementation in the app.
 */
export function ForfeitConfirmButton({
  isSolo,
  participantCount,
  isLoading,
  onConfirm,
  triggerClassName,
  triggerButtonProps,
}: ForfeitConfirmButtonProps) {
  const isManyPlayer = !isSolo && participantCount > 2;

  const title = isSolo
    ? 'Abandon this game?'
    : isManyPlayer
      ? 'Leave this game?'
      : 'Resign from this game?';
  const description = isSolo
    ? 'This game will end and be recorded as abandoned. This cannot be undone.'
    : isManyPlayer
      ? "You'll leave this game; the remaining players continue. This cannot be undone."
      : 'You will immediately lose this game, and your opponent will be recorded as the winner. This cannot be undone.';
  const confirmLabel = isSolo ? 'Abandon game' : isManyPlayer ? 'Leave game' : 'Resign';

  return (
    <ConfirmActionButton
      triggerLabel={isSolo ? 'Abandon' : isManyPlayer ? 'Leave' : 'Resign'}
      title={title}
      description={description}
      confirmLabel={confirmLabel}
      destructive
      isPending={isLoading}
      onConfirm={onConfirm}
      triggerClassName={triggerClassName}
      triggerButtonProps={triggerButtonProps}
    />
  );
}
